import React, { useCallback, useMemo, useState } from 'react'
import { useControlField, useField } from 'remix-validated-form'

import { FieldContainer } from '~/components'
import { cn } from '~/utils'
import { FieldErrorMessage } from '~/components'

type SelectFieldProps = {
  className?: string
  selectClassName?: string
  label?: string
  name: string
  required?: boolean
  disabled?: boolean
  options: string[] | { [key: string]: string }
  defaultNone?: boolean
  onBlur?: (e: React.FocusEvent<HTMLSelectElement, Element>) => void
  onChange?: (value: string) => void
}

const SelectField = React.memo(function SelectField(props: SelectFieldProps) {
  const {
    name,
    disabled,
    options,
    defaultNone = true,
    onBlur,
    onChange,
    selectClassName,
  } = props
  const { error, getInputProps, defaultValue } = useField(name)
  const [value, setValue] = useControlField<string>(name)
  const [hasInteracted, setHasInteracted] = useState(false)

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (disabled) {
        return
      }
      if (!hasInteracted) {
        setHasInteracted(true)
      }
      setValue(e.target.value)
      onChange?.(e.target.value)
    },
    [setValue, onChange, hasInteracted, disabled]
  )

  const optionElements = useMemo(() => {
    if (Array.isArray(options)) {
      return options.map((option, index) => (
        <option key={index} value={option}>
          {option}
        </option>
      ))
    } else {
      return Object.keys(options).map((key, index) => (
        <option key={index} value={key}>
          {options[key]}
        </option>
      ))
    }
  }, [options])

  const selectElemClassName = useMemo(() => {
    return cn(
      'flex h-10 w-full rounded-md border border-input bg-background py-2 pl-3 pr-5 text-base sm:text-sm ring-offset-background file:border-0 file:bg-transparent file:text-base sm:file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
      hasInteracted &&
        defaultValue &&
        value != defaultValue &&
        'ring-2 ring-yellow-300 ring-offset-2',
      hasInteracted &&
        !defaultValue &&
        value &&
        'ring-2 ring-green-400 ring-offset-2',
      disabled && 'pointer-events-none cursor-not-allowed opacity-50',
      selectClassName
    )
  }, [defaultValue, value, disabled, hasInteracted, selectClassName])

  return (
    <FieldContainer {...props} id={name}>
      <select
        {...getInputProps({
          id: name,
          value: value || '',
          onBlur: (e) => {
            if (!hasInteracted) {
              setHasInteracted(true)
            }
            onBlur?.(e)
          },
          onChange: handleChange,
        })}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : undefined}
        className={selectElemClassName}>
        {defaultNone && <option value="">None</option>}
        {optionElements}
      </select>
      <FieldErrorMessage error={error} />
    </FieldContainer>
  )
})

export default SelectField
